import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch } from '@nxsys/store';
import { statutoryActions } from '@nxsys/store/slices/statutory/reducer/statutorySlice';
import { ACTIONS_STATUTORY_TYPE, STATUTORY_PAYMENT_TYPE } from '@nxsys/constants/statutory';
import { Form, FormProps, Input } from 'antd';
import { toNumber } from 'lodash';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

type DetailActionsModalProps = {
  isOpen: boolean;
  title: string;
  actionType: string;
  toggle: () => void;
  refetchData: () => void;
  data: any;
};

const DetailActionsModal = ({
  isOpen,
  title,
  actionType,
  toggle,
  data,
  refetchData,
}: DetailActionsModalProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const [form] = Form.useForm();
  const { type = '' } = useParams();
  const { messages } = useTypedIntl();

  const FORM_ITEMS =
    actionType === ACTIONS_STATUTORY_TYPE.STOP ||
    actionType === ACTIONS_STATUTORY_TYPE.RESUME
      ? [
          {
            label: messages["statutory.detail.records.reason"],
            name: 'reason',
            type: 'textarea',
            required: true,
            placeholder: messages["statutory.detail.records.enterReason"],
          },
        ]
      : [
          {
            label: messages["statutory.detail.records.kitDayUsed"],
            name: 'keepingInTouchDays',
            type: 'number',
            required: true,
            placeholder: messages["statutory.detail.records.enterKitDayUsed"],
          },
        ];

  const onFinish: FormProps['onFinish'] = async (values) => {
    if (actionType === ACTIONS_STATUTORY_TYPE.STOP) {
      await dispatch(
        statutoryActions.stopStatutoryRecord({
          type: type as STATUTORY_PAYMENT_TYPE,
          id: data?.id,
          reason: values.reason,
        }),
      );
    }
    if (actionType === ACTIONS_STATUTORY_TYPE.RESUME) {
      await dispatch(
        statutoryActions.resumeStatutoryRecord({
          type: type as STATUTORY_PAYMENT_TYPE,
          id: data?.id,
          reason: values.reason,
        }),
      );
    }
    if (actionType === ACTIONS_STATUTORY_TYPE.EDIT) {
      await dispatch(
        statutoryActions.updateStatutoryRecord({
          type: type as STATUTORY_PAYMENT_TYPE,
          id: data?.id,
          keepingInTouchDays: toNumber(values?.keepingInTouchDays),
        }),
      );
    }
    refetchData();
    toggle();
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <AppModalContainer
      title={title}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages["common.cancel"],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages["common.submit"],
          buttonType: 'primary',
          onClick: () => form.submit(),
        },
      ]}
      minHeight="300px"
    >
      <AppFormContainer
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        layout="vertical"
        wrapperCol={{ span: 24 }}
      >
        {FORM_ITEMS.map((formItem) => (
          <Form.Item
            key={formItem.name}
            label={formItem.label}
            name={formItem.name}
            rules={[
              {
                required: formItem.required,
                message: messages["statutory.form.pleaseInput"],
              },
            ]}
          >
            {formItem.type === 'textarea' && (
              <Input.TextArea
                name={formItem.name}
                placeholder={formItem.placeholder}
                onChange={(e) => {
                  form.setFieldsValue({
                    [formItem.name]: e.target.value,
                  });
                }}
                rows={5}
              />
            )}
            {formItem.type === 'number' && (
              <AppInput
                name={formItem.name}
                placeholder={formItem.placeholder}
                onChange={(e) => {
                  form.setFieldsValue({
                    [formItem.name]: e.target.value,
                  });
                }}
                type={formItem.type}
              />
            )}
          </Form.Item>
        ))}
      </AppFormContainer>
    </AppModalContainer>
  );
};

export default DetailActionsModal;
